/**
 * This is the integration of Foundation Toggle, extended by some TUD specific functionality:
 * - the option "closeOnClick" closes the toggle with a click outside of the toggle
 * - some helper methods to get information (id, state) and immediately show / hide the toggle
 */

import $ from 'jquery';
import { Toggler as FoundationToggler } from 'foundation-sites/js/foundation.toggler';
import { MediaQuery } from 'foundation-sites/js/foundation.util.mediaQuery';

const animateInClass = 'toggle-animate-in';
const animateOutClass = 'toggle-animate-out';

export default class Toggler extends FoundationToggler {
    constructor(element, options) {
        super(element, options);

        this.options = $.extend({}, Toggler.defaults, this.$element.data(), options);

        this._bodyClickHandler = this._bodyClickHandler.bind(this);
        this._bodyKeydownHandler = this._bodyKeydownHandler.bind(this);
        this._bodyFocusoutHandler = this._bodyFocusoutHandler.bind(this);
    }

    /**
     * Get the HTML id of the toggle element
     * @returns {String}
     */
    get id() {
        return this.$element[0].id;
    }

    /**
     * Get the state of the toggle (whether the toggle is on)
     * @returns {Boolean}
     */
    get isOn() {
        if (this.options.animate) {
            return !this.$element.is(':hidden');
        }
        return this.$element.hasClass(this.className);
    }

    /**
     * Get the instances of all other Foundation Toggles that are nested inside this toggle
     * @returns {Array.<Toggler>}
     */
    get childToggles() {
        return Array.from(this.$element[0].querySelectorAll('[data-toggler]'))
            .map((childToggleElem) => $(childToggleElem).data('zfPlugin'));
    }

    /**
     * Get the instances of all other Foundation Toggles that are parents of this toggle
     * @returns {Array.<Toggler>}
     */
    get parentToggles() {
        return Array.from(document.querySelectorAll(`[data-toggler]:has(#${this.id}):not(#${this.id})`))
            .map((childToggleElem) => $(childToggleElem).data('zfPlugin'));
    }

    /**
     * Toggle on without animation
     */
    show() {
        if (this.options.animate && !this.$element.hasClass(animateInClass)) {
            this.$element.show();
            this._updateARIA(true);
            this._updateAnimateClasses(true);
            this.$element.trigger('on.zf.toggler');
        } else if (this.$element.hasClass(this.className)) {
            this._toggleClass();
        }
    }

    /**
     * Toggle off without animation
     */
    hide() {
        if (this.options.animate && (!this.$element.hasClass(animateOutClass))) {
            this.$element.hide();
            this._updateARIA(false);
            this._updateAnimateClasses(false);
            this.$element.trigger('off.zf.toggler');
        } else if (this.$element.hasClass(this.className)) {
            this._toggleClass();
        }
    }

    /**
     * Extends Foundation's _init method
     */
    _init() {
        // Remember responsive animate options
        if (typeof this.options.animate === 'object') {
            this.animateResponsive = Object.entries(this.options.animate);
            this.options.animate = 'unused';
        }

        super._init();
    }

    /**
     * Extends Foundation's _event method
     */
    _events() {
        // react on togglers with "data-open"
        this.$element.off('open.zf.trigger').on('open.zf.trigger', () => {
            if (!this.isOn) {
                this.toggle();
            }
        });

        // react on togglers with "data-close"
        this.$element.off('close.zf.trigger').on('close.zf.trigger', () => {
            if (this.isOn) {
                this.toggle();
            }
        });

        super._events();
    }

    /**
     * Adds support for responsive toggle animation to Foundation's _toggleAnimate method
     */
    _toggleAnimate() {
        // handle responsive animate options
        if (this.animateResponsive) {
            const breakpointFound = this.animateResponsive.some(([breakpoint, animation]) => {
                if (MediaQuery.is(breakpoint)) {
                    const input = animation.split(' ');
                    this.animationIn = input[0];
                    this.animationOut = input[1] || null;
                    return true;
                }
                return false;
            });
            if (!breakpointFound) {
                // there is no animation for this screen size, fall back to class toggle
                this._toggleClass();
                return;
            }
        }

        super._toggleAnimate();

        const hidden = this.$element.is(':hidden');
        this._updateAnimateClasses(hidden);
    }

    /**
     * Checks if one of the given triggers contains a given HTML element
     * @param {HTMLElement} target - the HTML element to check
     * @param {Array.<String>} triggers - the trigger that should be checked (can be 'open', 'close' and/or 'toggle')
     * @returns {Boolean}
     */
    triggerContains(target, triggers = ['open', 'close', 'toggle']) {
        const clickedTriggers = triggers.map((trigger) => (
            Array.from(
                // get all the triggers (togglers) for this toggle in the DOM
                document.querySelectorAll(`[data-${trigger}~="${this.id}"]`),
            ).filter(
                // we are interested in triggers that contain the target only
                (elem) => elem.contains(target),
            )
        )).flat();

        return clickedTriggers.length > 0;
    }

    /**
     * Checks if the toggle should close if user clicks on a given target
     * @param {HTMLElement} target - the HTML element to check
     * @returns {Boolean}
     */
    shouldCloseOnClick(target) {
        return (
            this.options.closeOnClick
            // clicks inside the toggle don't close it
            && !this.$element[0].contains(target)
            // clicks on an "open" toggler don't close it
            && !this.triggerContains(target, ['open'])
            // clicks on a "toggle" toggler don't close it if the toggle is off (it gets opened instead)
            && !(this.triggerContains(target, ['toggle']) && !this.isOn)
        );
    }

    /**
     * Click handler for clicks on body
     * Makes checks for nested toggles and closes this toggle if parent/child toggles don't handle the click
     * @param {Event} e - the click event
     *
     * @private
     */
    _bodyClickHandler(e) {
        // check if there is a parent toggle that gets closed by the click event
        const parentToggleWillClose = this.parentToggles.some((parentToggle) => (
            parentToggle.isOn && (
                parentToggle.shouldCloseOnClick(e.target) || parentToggle.triggerContains(e.target, ['close', 'toggle'])
            )
        ));

        // check if the toggler button of one of the child (nested) toggles got clicked
        const childToggleClicked = this.childToggles.some((childToggle) => (
            childToggle.triggerContains(e.target)
        ));

        if (!parentToggleWillClose
            && !childToggleClicked
            && this.shouldCloseOnClick(e.target)
            && !this.triggerContains(e.target)
        ) {
            this.$element.foundation('toggle');
        }
    }

    /**
     * Handle keydown events - closes the toggle on Esc keydown after some checks
     * @param {Event} e - the keydown event
     */
    _bodyKeydownHandler(e) {
        if (e.key !== 'Escape') {
            return;
        }

        // get all child (nested) toggles that should get closed by Esc, too
        const childToggleCloses = this.childToggles.some((childToggle) => (
            childToggle.isOn && childToggle.options.closeOnClick
        ));

        // if we found such a child toggle, we do nothing here
        if (childToggleCloses) {
            return;
        }

        // focus the first visible toggler of this toggle, prefer "toggle" over "open" togglers
        const openTogglers = [
            ...Array.from(document.querySelectorAll(`[data-toggle~="${this.id}"]`)),
            ...Array.from(document.querySelectorAll(`[data-open~="${this.id}"]`)),
        ].filter((togglerElem) => togglerElem.checkVisibility());
        if (openTogglers.length > 0) {
            openTogglers[0].focus();
        }

        this.$element.foundation('toggle');
    }

    /**
     * Handle focusout events - closes the toggle if the focus shifts from the toggle or toggler
     * to outside of the toggle or toggler
     * @param {Event} e - the keydown event
     */
    _bodyFocusoutHandler(e) {
        if (
            e.relatedTarget
            && (this.$element[0].contains(e.target) || this.triggerContains(e.target))
            && !(this.$element[0].contains(e.relatedTarget) || this.triggerContains(e.relatedTarget))
        ) {
            this.$element.foundation('toggle');
        }
    }

    /**
     * Extend Foundation's _updateARIA method, adds the click handler for the closeOnClick function
     * @param {Boolean} isOn - whether the toggle is on or off
     *
     * @private
     */
    _updateARIA(isOn) {
        super._updateARIA(isOn);

        if (this.options.closeOnClick) {
            if (isOn) {
                document.body.addEventListener('click', this._bodyClickHandler);
                document.body.addEventListener('keydown', this._bodyKeydownHandler);
                document.body.addEventListener('focusout', this._bodyFocusoutHandler);
            } else {
                document.body.removeEventListener('click', this._bodyClickHandler);
                document.body.removeEventListener('keydown', this._bodyKeydownHandler);
                document.body.removeEventListener('focusout', this._bodyFocusoutHandler);
            }
        }
    }

    /**
     * Adds animate classes to toggle and toggler and triggers respective events
     * @param {Boolean} isOn - whether the toggle is on or off
     *
     * @private
     */
    _updateAnimateClasses(isOn) {
        const $elems = this.$element.add(
            `[data-open~="${this.id}"], [data-close~="${this.id}"], [data-toggle~="${this.id}"]`,
        );

        if (isOn) {
            $elems.addClass(animateInClass).removeClass(animateOutClass);
            this.$element.trigger('animateIn.zf.toggler');
        } else {
            $elems.removeClass(animateInClass).addClass(animateOutClass);
            this.$element.trigger('animateOut.zf.toggler');
        }
    }

    /**
     * Unbind event handlers
     */
    _destroy() {
        document.body.removeEventListener('click', this._bodyClickHandler);
        document.body.removeEventListener('keydown', this._bodyKeydownHandler);
        document.body.removeEventListener('focusout', this._bodyFocusoutHandler);
        this.$element.off('open.zf.trigger');
        this.$element.off('close.zf.trigger');
    }
}

/**
 * Default settings
 *
 * @type {{closeOnClick: boolean}}
 */
Toggler.defaults = {
    // close the toggle element with a click on the body
    closeOnClick: false,
};
